/**
 * GuestNav — sticky responsive header for the guest booking SPA.
 *
 * Re-authored from design-book/project/booking/parts.jsx GuestNav.
 * Key adaptations:
 *  - TanStack `<Link>` for all navigation (no mock go() callback).
 *  - Tailwind `md:` responsive classes instead of the prototype's JS
 *    useMobile hook — both desktop and mobile branches are always in the
 *    DOM; CSS hides the inactive one.
 *  - "Book Now" is a `<Link>` styled via exported `buttonVariants` (no
 *    `<button>` nested inside `<a>` which would be invalid HTML and confuse
 *    ARIA roles).
 *  - Navy overlay close/cabinet buttons are plain `<button>` elements because
 *    the kit IconButton is themed to `text-primary` (navy on cream) and would
 *    be invisible on the navy overlay background.
 *  - Nav links target only REAL routes that exist in the router:
 *      "/" (home / apartment listing), "/cabinet"
 *    Prototype-only links (Neighbourhoods, About, Contact) are omitted until
 *    routes exist.
 */
import { useState, type JSX } from 'react';
import { Link } from '@tanstack/react-router';
import { Globe, UserRound, Menu, X, Mail } from 'lucide-react';
import { IconButton } from '@/components/ui/icon-button';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { company } from '@/lib/company';
import { m } from '@/paraglide/messages';

// ─── Logo compound (shared between desktop + mobile) ─────────────────────────
// The real brand monogram (oval O·L crest, public/ollio-monogram-navy.png)
// paired with the serif uppercase "OLLIO" wordmark — matching the design.

function OllioLogo({ size = 'md' }: { size?: 'sm' | 'md' }) {
  return (
    <>
      {/* Real brand monogram (the parent Link carries the accessible name, so
          the image is decorative). */}
      <img
        src="/ollio-monogram-navy.png"
        alt=""
        className={size === 'sm' ? 'h-8 w-auto' : 'h-10 w-auto'}
      />
      <span
        className={cn(
          'font-serif font-semibold text-[var(--ollio-navy)] tracking-[0.04em]',
          size === 'sm' ? 'text-xl' : 'text-2xl',
        )}
      >
        OLLIO
      </span>
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

// Shared styling for the overlay's primary links — one place, so a link added
// later cannot quietly land in a different size.
const MOBILE_LINK =
  'font-serif text-[30px] font-semibold text-white py-2.5 border-b border-white/10 no-underline hover:text-white/80 transition';

export function GuestNav(): JSX.Element {
  const [open, setOpen] = useState(false);
  const close = () => setOpen(false);

  return (
    <>
      {/* Shared sticky wrapper */}
      <div
        className={cn(
          'sticky top-0 z-40',
          'bg-[rgba(250,250,248,0.88)] backdrop-blur-[10px]',
          'border-b border-[var(--border-hairline)]',
        )}
      >
        {/* ── Desktop header (md and up) ────────────────────────────────────── */}
        <header
          aria-label="Site navigation"
          className="hidden md:flex h-[72px] items-center gap-8 px-10"
        >
          {/* Logo */}
          <Link to="/" aria-label="OLLIO — home" className="flex items-center gap-2.5">
            <OllioLogo />
          </Link>

          {/* Right-side actions */}
          <div className="ml-auto flex items-center gap-4">
            {/* Language slot — reserved, non-functional */}
            <button
              disabled
              aria-label="Language"
              className="flex items-center gap-1.5 cursor-not-allowed opacity-70 text-muted text-[13px] font-semibold"
            >
              <Globe className="h-[17px] w-[17px]" />
              EN
            </button>

            {/* Hairline divider */}
            <span className="h-5 w-px bg-[var(--border-hairline)]" aria-hidden="true" />

            {/* My Cabinet */}
            <Link
              to="/cabinet"
              aria-label="My Cabinet"
              className="flex items-center gap-1.5 text-sm font-semibold text-[var(--ollio-navy)]"
            >
              <UserRound className="h-[18px] w-[18px]" />
              My Cabinet
            </Link>

            {/* Book Now — Link styled as gold pill button (no button-in-link nesting) */}
            <Link
              to="/"
              className={buttonVariants({ variant: 'gold', pill: true, size: 'sm' })}
            >
              Book Now
            </Link>
          </div>
        </header>

        {/* ── Mobile header (below md) ──────────────────────────────────────── */}
        <header
          aria-label="Site navigation"
          className="flex md:hidden h-[60px] items-center gap-2.5 px-4"
        >
          {/* Logo */}
          <Link to="/" aria-label="OLLIO — home" className="flex items-center gap-2">
            <OllioLogo size="sm" />
          </Link>

          {/* Right-side actions */}
          <div className="ml-auto flex items-center gap-1">
            {/* My Cabinet — icon link; plain Link styled as an icon button to avoid
                button-in-link nesting; aria-label gives the accessible name */}
            <Link
              to="/cabinet"
              aria-label="My Cabinet"
              className="inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-control)] text-primary transition hover:bg-[var(--ollio-navy-light)]"
            >
              <UserRound className="h-[20px] w-[20px]" />
            </Link>

            {/* Menu — opens overlay */}
            <IconButton label="Menu" variant="ghost" onClick={() => setOpen(true)}>
              <Menu className="h-[20px] w-[20px]" />
            </IconButton>
          </div>
        </header>
      </div>

      {/* ── Mobile fullscreen overlay ──────────────────────────────────────── */}
      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className="fixed inset-0 z-[60] bg-[var(--ollio-navy)] text-white p-5 flex flex-col"
        >
          {/* Top row: wordmark + close */}
          <div className="flex items-center">
            <span className="font-serif text-[22px] font-semibold tracking-[0.04em] text-white">
              OLLIO
            </span>
            {/* Plain button — kit IconButton is navy-themed and invisible on navy bg */}
            <button
              type="button"
              aria-label="Close"
              onClick={close}
              className="ml-auto inline-flex h-10 w-10 items-center justify-center rounded-[var(--radius-control)] text-white transition hover:bg-white/10"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Nav links — only real routes.
              This was one link, "Apartments", so a guest on a phone had no way
              to reach help or the legal documents at all: the footer carries
              them, but the overlay covers the page, and someone who opened the
              menu looking for them found a dead end. */}
          <nav className="flex flex-col gap-1 mt-9" aria-label="Mobile navigation">
            <Link
              to="/"
              onClick={close}
              className={MOBILE_LINK}
            >
              Apartments
            </Link>
            <Link to="/help" onClick={close} className={MOBILE_LINK}>
              Help
            </Link>
            <Link
              to="/legal/$slug"
              params={{ slug: 'terms' }}
              onClick={close}
              className={MOBILE_LINK}
            >
              Terms
            </Link>
            <Link
              to="/legal/$slug"
              params={{ slug: 'privacy' }}
              onClick={close}
              className={MOBILE_LINK}
            >
              Privacy
            </Link>
            <Link
              to="/legal/$slug"
              params={{ slug: 'cancellation' }}
              onClick={close}
              className={MOBILE_LINK}
            >
              Cancellation
            </Link>
          </nav>

          {/* Contact. Same address and the same stated turnaround as the
              footer, from the same module — a guest who opens the menu to ask
              a question should not have to close it again to find out how. */}
          <a
            href={`mailto:${company.bookingsEmail}`}
            className="mt-7 inline-flex items-center gap-2.5 text-base font-semibold text-white/85 no-underline transition hover:text-white"
          >
            <Mail className="h-5 w-5" />
            {company.bookingsEmail}
          </a>
          <p className="mt-1.5 text-sm text-white/55">{m.footer_response_time()}</p>

          {/* Bottom actions */}
          <div className="mt-auto flex flex-col gap-3">
            {/* My Cabinet — plain link (navy overlay = plain styled link) */}
            <Link
              to="/cabinet"
              onClick={close}
              className="flex items-center gap-2.5 text-white/85 text-base font-semibold no-underline hover:text-white transition"
            >
              <UserRound className="h-5 w-5" />
              My Cabinet
            </Link>

            {/* Book Now — gold pill full-width */}
            <Link
              to="/"
              onClick={close}
              className={cn(
                buttonVariants({ variant: 'gold', pill: true, size: 'md' }),
                'w-full justify-center',
              )}
            >
              Book Now
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
